import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle, AlertCircle, Clock, Loader2,
  Users, Send, XCircle, RefreshCw, Filter
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../lib/permissions';

interface Batch {
  id: number;
  name: string;
  campaignName?: string;
  targetType?: string;
  templateContent?: string;
  mediaUrl?: string;
  mediaType?: string;
  targetCount: number;
  sentCount: number;
  failedCount: number;
  totalRecipients: number;
  status: string;
  scheduledAt: number;
  startedAt?: number;
  completedAt?: number;
  hourlyCap?: number;
  minIntervalSeconds?: number;
  maxIntervalSeconds?: number;
  createdAt: number;
}

interface BatchLog {
  id: number;
  customerPhone: string;
  customerName?: string;
  messageContent: string;
  status: string;
  errorMessage?: string;
  sentAt?: number;
  createdAt: number;
  versionLabel?: string;
}

function formatDate(ts: number | string): string {
  if (!ts) return '-';
  const d = new Date(typeof ts === 'string' && ts.length > 10 ? ts : Number(ts) * 1000);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('zh-HK');
}

function formatDateTime(ts: number | string): string {
  if (!ts) return '-';
  const d = new Date(typeof ts === 'string' && ts.length > 10 ? ts : Number(ts) * 1000);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString('zh-HK', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function statusBadge(status: string) {
  if (status === 'completed') {
    return <span className="inline-flex items-center px-2.5 py-1 bg-green-100 text-green-700 rounded text-xs font-medium dark:bg-green-900/20 dark:text-green-300"><CheckCircle className="w-3 h-3 mr-1" /> 已完成</span>;
  }
  if (status === 'sending') {
    return <span className="inline-flex items-center px-2.5 py-1 bg-orange-100 text-orange-700 rounded text-xs font-medium dark:bg-orange-900/20 dark:text-orange-300"><Loader2 className="w-3 h-3 mr-1 animate-spin" /> 發送中</span>;
  }
  if (status === 'pending') {
    return <span className="inline-flex items-center px-2.5 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium dark:bg-blue-900/20 dark:text-blue-300"><Clock className="w-3 h-3 mr-1" /> 待發送</span>;
  }
  if (status === 'cancelled') {
    return <span className="inline-flex items-center px-2.5 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium dark:bg-gray-700 dark:text-gray-300"><XCircle className="w-3 h-3 mr-1" /> 已取消</span>;
  }
  return <span className="inline-flex items-center px-2.5 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium dark:bg-gray-700 dark:text-gray-300">{status}</span>;
}

const AdminBroadcastDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const token = localStorage.getItem('admin_token');
  const { can } = usePermissions();

  const [batch, setBatch] = useState<Batch | null>(null);
  const [logs, setLogs] = useState<BatchLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [logFilter, setLogFilter] = useState<'all' | 'sent' | 'failed' | 'pending'>('all');
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchDetail = async () => {
    if (!id) return;
    try {
      const res = await apiFetch(`/api/public/admin/broadcast-batches/${id}`);
      if (!res.ok) throw new Error('載入失敗');
      const data = await res.json();
      setBatch(data.batch);
      setLogs(data.logs || []);
    } catch (e: any) {
      setError(e.message || '載入失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) { navigate('/admin'); return; }
    fetchDetail();
  }, [id]);

  useEffect(() => {
    if (batch?.status === 'sending') {
      refreshTimerRef.current = setInterval(fetchDetail, 5000);
    }
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [batch?.status]);

  const total = batch?.targetCount || batch?.totalRecipients || 0;
  const done = (batch?.sentCount || 0) + (batch?.failedCount || 0);
  const progress = total > 0 ? Math.round((done / total) * 100) : 0;

  const filteredLogs = logs.filter(l => logFilter === 'all' || l.status === logFilter);

  const handleCancel = async () => {
    if (!batch || batch.status !== 'pending') return;
    if (!confirm('確定要取消這個任務嗎？')) return;
    try {
      const res = await apiFetch(`/api/public/admin/broadcast-batches/${batch.id}`, {
        method: 'PUT',
        body: JSON.stringify({ status: 'cancelled' })
      });
      if (res.ok) {
        fetchDetail();
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || '取消失敗');
      }
    } catch (e: any) {
      setError(e.message || '取消失敗');
    }
  };

  const handleLogout = () => {
    if (confirm('確定要登出嗎？')) {
      localStorage.removeItem('admin_token');
      navigate('/admin');
    }
  };

  return (
    <AdminLayout currentPage="broadcast" onLogout={handleLogout}>
      <div>
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/broadcast')}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-orange-600 dark:hover:text-orange-400 text-sm mb-2"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> 返回廣播推廣
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            {loading ? '載入中...' : (batch?.name || '任務詳情')}
          </h1>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
            {error}
          </div>
        )}

        {loading ? (
          <div className="p-12 text-center text-gray-500 dark:text-gray-400">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
            載入中...
          </div>
        ) : !batch ? (
          <div className="p-12 text-center text-gray-500 dark:text-gray-400">任務不存在</div>
        ) : (
          <div className="space-y-6">
            {/* Status banner */}
            <div className={`rounded-lg p-4 flex items-center justify-between ${
              batch.status === 'completed' ? 'bg-green-50 border border-green-200 dark:bg-green-900/10 dark:border-green-800' :
              batch.status === 'sending' ? 'bg-orange-50 border border-orange-200 dark:bg-orange-900/10 dark:border-orange-800' :
              batch.status === 'pending' ? 'bg-blue-50 border border-blue-200 dark:bg-blue-900/10 dark:border-blue-800' :
              'bg-gray-50 border border-gray-200 dark:bg-gray-800 dark:border-gray-700'
            }`}>
              <div className="flex items-center gap-3">
                {batch.status === 'completed' ? <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" /> :
                 batch.status === 'sending' ? <Loader2 className="w-6 h-6 text-orange-600 dark:text-orange-400 animate-spin" /> :
                 batch.status === 'pending' ? <Clock className="w-6 h-6 text-blue-600 dark:text-blue-400" /> :
                 <XCircle className="w-6 h-6 text-gray-600 dark:text-gray-400" />}
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {batch.status === 'completed' ? '發送已完成' :
                     batch.status === 'sending' ? '正在發送中...' :
                     batch.status === 'pending' ? '待發送' :
                     '已取消'}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {batch.status === 'completed' ? `完成於 ${formatDateTime(batch.completedAt || 0)}` :
                     batch.status === 'sending' ? `開始於 ${formatDateTime(batch.startedAt || 0)}` :
                     batch.status === 'pending' ? `預計 ${formatDateTime(batch.scheduledAt || batch.createdAt)} 發送` :
                     '任務已被取消'}
                  </p>
                </div>
              </div>
              {batch.status === 'pending' && can(PERMISSIONS.BROADCAST_SEND) && (
                <button
                  onClick={handleCancel}
                  className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                >
                  取消任務
                </button>
              )}
            </div>

            {/* Stats cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  <span className="text-xs text-gray-500 dark:text-gray-400">總收件人</span>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{total}</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Send className="w-4 h-4 text-green-500" />
                  <span className="text-xs text-gray-500 dark:text-gray-400">成功</span>
                </div>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{batch.sentCount || 0}</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                <div className="flex items-center gap-2 mb-1">
                  <AlertCircle className="w-4 h-4 text-red-500" />
                  <span className="text-xs text-gray-500 dark:text-gray-400">失敗</span>
                </div>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">{batch.failedCount || 0}</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                <div className="flex items-center gap-2 mb-1">
                  <RefreshCw className="w-4 h-4 text-orange-500" />
                  <span className="text-xs text-gray-500 dark:text-gray-400">完成度</span>
                </div>
                <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{progress}%</p>
              </div>
            </div>

            {/* Progress bar */}
            {batch.status !== 'pending' && batch.status !== 'cancelled' && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                  <span>發送進度</span>
                  <span>{done} / {total}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 dark:bg-gray-700">
                  <div
                    className="bg-orange-600 h-3 rounded-full transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Task info */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">任務資訊</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-500 dark:text-gray-400">模板：</span><span className="text-gray-800 dark:text-gray-200">{batch.campaignName || '-'}</span></div>
                <div><span className="text-gray-500 dark:text-gray-400">目標類型：</span><span className="text-gray-800 dark:text-gray-200">{batch.targetType || '-'}</span></div>
                <div><span className="text-gray-500 dark:text-gray-400">每小時上限：</span><span className="text-gray-800 dark:text-gray-200">{batch.hourlyCap || 100} 則</span></div>
                <div><span className="text-gray-500 dark:text-gray-400">發送間隔：</span><span className="text-gray-800 dark:text-gray-200">{batch.minIntervalSeconds || 30}-{batch.maxIntervalSeconds || 60} 秒</span></div>
              </div>
              {batch.templateContent && (
                <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                  {batch.templateContent}
                </div>
              )}
              {batch.mediaUrl && batch.mediaType && (
                <div className="mt-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-medium px-2 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded">{batch.mediaType}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 truncate">{batch.mediaUrl}</span>
                  </div>
                  {batch.mediaType === 'image' && (
                    <img src={batch.mediaUrl} alt="media" className="max-h-48 rounded border border-gray-200 dark:border-gray-700 object-contain" />
                  )}
                </div>
              )}
            </div>

            {/* Logs */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">發送記錄 ({filteredLogs.length})</h3>
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  <select
                    value={logFilter}
                    onChange={e => setLogFilter(e.target.value as any)}
                    className="px-2 py-1 border border-gray-300 rounded text-sm dark:bg-gray-800 dark:text-white dark:border-gray-600"
                  >
                    <option value="all">全部</option>
                    <option value="sent">成功</option>
                    <option value="failed">失敗</option>
                    <option value="pending">待發送</option>
                  </select>
                </div>
              </div>
              <div className="overflow-x-auto">
                {filteredLogs.length === 0 ? (
                  <div className="p-8 text-center text-gray-500 dark:text-gray-400">暫無記錄</div>
                ) : (
                  <table className="w-full text-sm text-left">
                    <thead className="bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 uppercase text-xs">
                      <tr>
                        <th className="px-4 py-3">客戶</th>
                        <th className="px-4 py-3">電話</th>
                        <th className="px-4 py-3">版本</th>
                        <th className="px-4 py-3">狀態</th>
                        <th className="px-4 py-3">時間</th>
                        <th className="px-4 py-3">錯誤</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredLogs.map(log => (
                        <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-4 py-3 text-gray-900 dark:text-white">{log.customerName || '-'}</td>
                          <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{log.customerPhone}</td>
                          <td className="px-4 py-3">
                            {log.versionLabel ? (
                              <span className="inline-flex items-center px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded font-medium dark:bg-orange-900/30 dark:text-orange-300">
                                版本 {log.versionLabel}
                              </span>
                            ) : (
                              <span className="text-gray-400 dark:text-gray-500 text-xs">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {log.status === 'sent' ? (
                              <span className="text-green-600 dark:text-green-400 flex items-center gap-1 text-xs"><CheckCircle className="w-3 h-3" /> 成功</span>
                            ) : log.status === 'failed' ? (
                              <span className="text-red-600 dark:text-red-400 flex items-center gap-1 text-xs"><AlertCircle className="w-3 h-3" /> 失敗</span>
                            ) : (
                              <span className="text-gray-500 dark:text-gray-400 flex items-center gap-1 text-xs"><Clock className="w-3 h-3" /> 待發送</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{formatDateTime(log.sentAt || 0)}</td>
                          <td className="px-4 py-3 text-red-500 text-xs max-w-xs truncate">{log.errorMessage || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminBroadcastDetail;
